import { XIcon } from "@heroicons/react/outline";
import Button from "components/lib/Shared/Button";
import ChangesContext from "context/ChangesContext";
import TransactionEditContext from "context/TransactionEditContext";
import { useToast } from "hooks/useToast";
import { useContext, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import ManageChangeOfParticularsTable from "./ManageChangeOfParticularsTable";
import { Roles_Permissions_Constants } from "config/roles-permissions";
import { ManageModuleContext } from "context/ManageCompanyModuleContext";
import { Constants } from "config/constants";

const ChangeOfParticulars = ({ companyId, formFields }) => {
  const {
    company,
    companyOfficials,
    dashboardPermissions,
    dashboardRoles,
    designation,
  } = useSelector((state) => state.companyDashboardSlice);

  const currentUser = useSelector((state) => state.userSlice);
  const companyDashboardSlice = useSelector(
    (state) => state.companyDashboardSlice
  );

  const history = useHistory();
  const dispatch = useDispatch();
  const { toast } = useToast(3000);
  const { changeOfParticularsInfo, setChangeOfParticularsInfo, transactionId } =
    useContext(ManageModuleContext);

  const {
    isInTransactionEditContext,
    setChangeOfParticularsInfoEdit,
    transactionToEdit,
  } = useContext(TransactionEditContext);

  const [isLoading, setIsLoading] = useState(true);
  const [changeList, setChangeList] = useState([]);

  const [officialsList, setOfficialsList] = useState(null);
  const [selectedValueOfParticulars, setSelectedValueOfParticulars] =
    useState(null);
  const [updateTable, setUpdateTable] = useState(false);

  //const companyOfficials = companyDashboardSlice.companyOfficials.items;
  const { totalCount } = companyDashboardSlice.companyOfficials;

  const hasEditOtherDataPermission = dashboardPermissions.some(
    (permission) =>
      permission.name ===
      Roles_Permissions_Constants.DASHBOARD_PERMISSIONS.EDIT_OTHER_DATA
  );

  const handleChangeOfParticularValue = async (e) => {
    setSelectedValueOfParticulars(e.target.value);
    setChangeOfParticularsInfo({
      ...changeOfParticularsInfo,
      selectedValueType: e.target.value,
    });
    const initiateTransactionObj = {
      changeType: formFields.idTypeSelection,
      applicationType: formFields.officialMethod,
      selectType: e.target.value,
    };
    window.localStorage.setItem(
      "initiateTransactionObj",
      JSON.stringify(initiateTransactionObj)
    );
  };

  const { initiatedTransactions, getInitiatedTransactions } = useSelector(
    (state) => state.initiatedTransactionsSlice
  );

  useEffect(() => {
    if (officialsList === null && !isLoading) {
      toast(
        "error",
        `Please appoint ${formFields.officialMethod} to proceed further.`
      );
    }
    const fetchData = async () => {
      try {
        // Filter out officials already in initiatedTransactions
        const officialDesignation = initiatedTransactions.items
          .filter(
            (tx) =>
              tx.changeType === "CHANGE-OF-PARTICULARS" &&
              tx.status !== Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.PROCESSED
          )
          .flatMap((tx) => tx.officials || [])
          .map((official) => official.designation);
        const officialIds = initiatedTransactions.items
          .filter(
            (tx) =>
              tx.changeType === "CHANGE-OF-PARTICULARS" &&
              tx.status !== Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.PROCESSED
          )
          .flatMap((tx) => tx.officials || [])
          .map((official) => official._id || official);
        const filteredOfficials = companyOfficials.items.filter(
          (official) =>
            !officialIds.includes(official._id) &&
            (isInTransactionEditContext
              ? official.designation === officialDesignation[0]
              : formFields?.officialMethod
              ? official.designation === formFields.officialMethod
              : official.designation === formFields.designation)
        );

        setOfficialsList(filteredOfficials);
        if (isInTransactionEditContext && transactionToEdit?.officials) {
          // Filter officials for the transaction being edited
          const transactionOfficialIds = transactionToEdit.officials.map(
            (official) => official._id
          );
          const transactionToEditOfficials = companyOfficials.items.filter(
            (official) => transactionOfficialIds.includes(official._id)
          );
          // Set selected value based on transactionToEdit officials
          const selectedValue = transactionToEditOfficials.some(
            (official) =>
              official.changedFields?.electronicAddress?.mobileNumber ||
              official.changedFields?.electronicAddress?.emailAddress
          )
            ? "changes-including-email"
            : "changes-excluding-email";

          setSelectedValueOfParticulars(selectedValue);
          setChangeList(transactionToEditOfficials);
          setChangeOfParticularsInfoEdit({
            selectedValueTypeEdit: selectedValue,
          });
        }
        setIsLoading(false);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [initiatedTransactions, updateTable]);

  const submitApplication = async () => {
    history.push(
      `/companies/${companyId}/dashboard/transactions/${
        transactionId || transactionToEdit?._id
      }/recent-changes`
    );
  };

  return (
    <>
      {isLoading && <>loading...</>}
      {!isLoading &&
        (officialsList === null || officialsList?.length < 1) &&
        selectedValueOfParticulars === null &&
        !isInTransactionEditContext && (
          <div className="notification-container flex justify-center px-4 py-10 mt-2 mb-2 bg-gray-200 rounded-sm shadow-sm sm:rounded md:rounded-md relative">
            <h2 className="text-gray-900 font-normal">
              You have no assigned {formFields.officialMethod} to proceed with
              the workflow
            </h2>
            <span className="close-icon absolute top-2 right-2 cursor-pointer text-lg hover:bg-gray-300">
              <XIcon className="h-6 w-6" />
            </span>
          </div>
        )}
      {(officialsList?.length > 0 ||
        (officialsList >= 0 && selectedValueOfParticulars) ||
        isInTransactionEditContext) &&
        !isLoading && (
          <>
            <h2 className="font-semibold text-grey-700"> Select Type: </h2>
            <div className="flex justify-between px-4 py-6 mt-2 bg-white rounded-sm shadow-sm sm:rounded md:rounded-md">
              <div className="flex items-center space-x-2 ">
                <input
                  id="changes-excluding-email"
                  type="radio"
                  value="changes-excluding-email"
                  checked={
                    selectedValueOfParticulars === "changes-excluding-email"
                  }
                  disabled={isInTransactionEditContext}
                  onChange={handleChangeOfParticularValue}
                  className="w-4 h-4 cursor-pointer text-blue-600 bg-gray-100 border-gray-300 focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                />
                <label
                  htmlFor="changes-excluding-email"
                  className="radio-label flex cursor-pointer"
                >
                  Changes excluding e-mail or telephone number
                </label>
              </div>
              <div className="flex items-center space-x-2">
                <input
                  id="changes-including-email"
                  type="radio"
                  value="changes-including-email"
                  checked={
                    selectedValueOfParticulars === "changes-including-email" ||
                    changeList.some(
                      (changelist) =>
                        changelist.electronicAddress?.mobileNumber ||
                        changelist.electronicAddress?.emailAddress
                    )
                  }
                  disabled={isInTransactionEditContext}
                  onChange={handleChangeOfParticularValue}
                  className="w-4 h-4 cursor-pointer text-blue-600 bg-gray-100 border-gray-300 focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                />
                <label
                  htmlFor="changes-including-email"
                  className="radio-label flex cursor-pointer"
                >
                  Changes to include e-mail or telephone number
                </label>
              </div>
            </div>
          </>
        )}

      {selectedValueOfParticulars &&
        (officialsList?.length >= 0 || isInTransactionEditContext) && (
          <>
            <div className="mt-4">
              <h4 className="text-gray-800 font-semibold mt-2 mb-2">
                Officials List
              </h4>
              <div>
                <ManageChangeOfParticularsTable
                  companyId={companyId}
                  data={
                    hasEditOtherDataPermission
                      ? officialsList.filter(
                          (official) =>
                            !changeList.some(
                              (changeItem) => official._id === changeItem._id
                            )
                        )
                      : officialsList.filter(
                          (official) =>
                            official?.primarySource === currentUser?._id &&
                            !changeList.some(
                              (changeItem) =>
                                currentUser?._id === changeItem.primarySource
                            )
                        )
                  }
                  totalCount={totalCount}
                  currentTab="stakeholderTab"
                  changeList={changeList}
                  setChangeList={setChangeList}
                  typeOfTable="changeOfParticulars"
                  isOfficialsListTable
                />
              </div>
            </div>
          </>
        )}

      {changeList.length > 0 && (
        <div className="flex flex-col">
          <h4 className="text-gray-800 font-semibold mt-2 mb-2">
            Change Of Particulars
          </h4>
          <ManageChangeOfParticularsTable
            companyId={companyId}
            data={changeList}
            totalCount={changeList.length}
            currentTab="stakeholderTab"
            dataRowStyle="bg-[#f2f8e9]"
            changeList={changeList}
            setChangeList={setChangeList}
            setOfficialsList={setOfficialsList}
            isChangeOfParticularsTable
            updateTable={updateTable}
            setUpdateTable={setUpdateTable}
          />
          <div className="flex justify-end">
            <Button
              type="submit"
              className="flex items-center px-4 py-2 bg-sflPrimary mb-2 font-semibold text-white rounded mt-7 disabled:cursor-not-allowed"
              onClick={submitApplication}
            >
              Proceed
            </Button>
          </div>
        </div>
      )}
    </>
  );
};

export default ChangeOfParticulars;
